// Dashboard
import { CUR, ccySym, effFx, grandTotal, projectCcy, secTotals } from '../core/budget.js';
import { useEffect, useMemo, useState } from '../core/react.js';
import { API } from '../core/api.js';
import { costsSummary } from '../core/costs-summary.js';
import { MONO, T, fmt } from '../core/theme.js';
import { Badge, Pill, ST } from '../ui/widgets.js';
import { Icon } from '../ui/icons.js';
import { ComingUp } from './deadlines.js';

export function Dashboard({
  projects,
  project,
  sections,
  fx,
  setView,
  switchProject,
  onAddProject
}) {
  const gt = useMemo(() => grandTotal(sections, fx), [sections, fx]);
  // the costs entered on the Costs page (invoiced, paid or not), for "Costs in"
  const [invoices, setInvoices] = useState(null);
  useEffect(() => {
    let live = true;
    if (!project?.id) { setInvoices([]); return; }
    API.call('invoices.list', { projectId: project.id }).then(r => live && setInvoices(r.invoices)).catch(() => live && setInvoices([]));
    return () => { live = false; };
  }, [project?.id, project?.version]);
  // each project's costs in brief, for the project list
  const [allCosts, setAllCosts] = useState(null);
  useEffect(() => { API.call('invoices.totals').then(r => setAllCosts(r.costs)).catch(() => setAllCosts({})); }, []);
  const spent = useMemo(() => invoices && costsSummary(invoices, sections, fx, 'usd'), [invoices, sections, fx]);   // billing currency, beside out-cost and margin
  const done = project?.status === 'Complete';
  const finalGap = spent && spent.count ? spent.net - spent.budget : null;
  const allItems = sections.flatMap(s => s.items);
  const stCounts = allItems.reduce((a, i) => {
    a[i.st] = (a[i.st] || 0) + 1;
    return a;
  }, {});
  const pColors = [T.navy, T.blue, T.amber, T.purple, T.green];
  const metrics = [{
    // in the billing currency like the other tiles; the cost currency (the agency's P&L) underneath when it differs
    label: 'Total In-Cost',
    v: fmt.usd(gt.rawUSD),
    sub: CUR.cost === CUR.bill ? CUR.bill + ' · agency P&L' : `${CUR.bill} · ${fmt.gbp(gt.inGBP)} in ${CUR.cost}, the agency P&L`,
    bdr: T.navy
  }, {
    label: 'Total Out-Cost',
    v: fmt.usd(gt.outUSD),
    sub: CUR.bill + ' · client billing',
    bdr: T.navyM
  }, {
    // what the margin comes to if costs land as budgeted: it is only known once every cost is in
    label: 'Projected Margin',
    v: fmt.usd(gt.margUSD),
    sub: gt.outUSD > 0 ? fmt.pct(gt.margUSD / gt.outUSD) + ' of out-cost, if costs land on budget' : '—',
    bdr: T.navyL
  }, {
    // every cost entered that isn't void, invoiced whether paid or not, before tax; over / under budget overall only
    // once the project is Complete, since until then the rest of the costs are still to come
    label: 'Costs In',
    v: !spent ? '…' : spent.count ? fmt.usd(spent.net) : 'No costs yet',
    sub: !spent ? '' : !spent.count ? 'Add invoices and receipts on Costs' :
      done ? (finalGap > 0.005 ? `Final: over budget by ${fmt.usd(finalGap)}` : `Final: under budget by ${fmt.usd(-finalGap)}`) :
      `${spent.share != null ? fmt.pct(spent.share, 0) + ' of ' : ''}${fmt.usd(spent.budget)} budget`,
    notes: spent ? [
      spent.count > 0 && { t: `Paid: ${fmt.usd(spent.paid)} (${spent.paidCount} of ${spent.count})`, c: T.slate },
      spent.over.length > 0 && { t: `${spent.over.length} line${spent.over.length > 1 ? 's' : ''} already over budget (+${fmt.usd(spent.overTotal)})`, c: T.red },
      spent.notCounted > 0.005 && { t: `${fmt.usd(spent.notCounted)} of costs not on a budget line`, c: T.amber },
      spent.noRate.length > 0 && { t: `Costs in ${spent.noRate.join(', ')} left out: no exchange rate`, c: T.amber }].filter(Boolean) : [],
    bdr: !spent || !spent.count ? T.mist : (done ? finalGap > 0.005 : spent.over.length) ? T.red : T.green,
    color: done && finalGap > 0.005 ? T.red : T.coal,
    onClick: () => setView('invoices')
  }];
  return React.createElement("div", {
    style: {
      padding: '30px 34px',
      overflowY: 'auto',
      height: '100%',
      background: T.ivory
    },
    className: "fade-up"
  }, React.createElement("div", {
    className: 'grid-2',
    style: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4,1fr)',
      gap: 14,
      marginBottom: 30
    }
  }, metrics.map((m, i) => React.createElement("div", {
    key: m.label,
    className: "fade-up",
    style: {
      animationDelay: `${i * 50}ms`,
      background: T.white,
      padding: '20px 22px',
      borderLeft: `3px solid ${m.bdr}`,
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.1em',
      color: T.mist,
      marginBottom: 10
    }
  }, m.label), React.createElement("div", {
    style: {
      ...MONO,
      fontSize: 22,
      fontWeight: 500,
      color: m.color || T.coal,
      marginBottom: 5
    }
  }, m.v), React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.mist
    }
  }, m.sub), (m.notes || []).map((n, j) => React.createElement("div", {
    key: j,
    style: { fontSize: 11, color: n.c, marginTop: 4 }
  }, n.t)), m.onClick && (m.notes || []).length > 0 && React.createElement("button", {
    onClick: m.onClick,
    style: { fontSize: 11, color: T.navyL, textDecoration: 'underline', marginTop: 4, padding: 0 }
  }, 'See costs')))), React.createElement(ComingUp, { projects, switchProject, setView }), React.createElement("div", {
    className: 'grid-stack',
    style: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: 20
    }
  }, React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: T.mist,
      marginBottom: 14
    }
  }, "PROJECTS"), React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12
    }
  }, projects.map((p, i) => React.createElement("div", {
    key: p.id,
    className: "fade-up",
    style: {
      animationDelay: `${150 + i * 60}ms`,
      background: T.white,
      padding: '18px 22px',
      cursor: 'pointer',
      boxShadow: '0 1px 4px rgba(62,63,67,.06)',
      transition: 'transform .2s,box-shadow .2s',
      borderTop: `3px solid ${pColors[i]}`
    },
    onClick: () => switchProject(p),
    onMouseEnter: e => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 20px rgba(62,63,67,.12)';
    },
    onMouseLeave: e => {
      e.currentTarget.style.transform = '';
      e.currentTarget.style.boxShadow = '0 1px 4px rgba(62,63,67,.06)';
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 10
    }
  }, React.createElement("div", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: T.coal,
      lineHeight: 1.4
    }
  }, p.name), React.createElement(Pill, {
    label: p.status,
    color: pColors[i],
    bg: pColors[i] + '15'
  })), React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.mist,
      marginBottom: p.sections.length > 0 ? 14 : 6
    }
  }, p.client, " \xB7 ", p.date), p.sections.length > 0 && (() => {
    const pc = projectCcy(p), pfx = effFx(p);
    const pgt = grandTotal(p.sections, pfx);
    const ps = allCosts && costsSummary(allCosts[p.id] || [], p.sections, pfx, 'usd');
    const pf = { out: n => ccySym(pc.bill) + fmt.num(n) };
    // all in the billing currency, as in the tiles at the top
    return React.createElement("div", {
      style: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(105px, 1fr))',
        gap: 14
      }
    }, [[pf.out(pgt.outUSD), 'Out-Cost ' + pc.bill], [pf.out(pgt.rawUSD), 'In-Cost ' + pc.bill], [pf.out(pgt.margUSD), 'Projected margin'],
      [!ps ? '…' : pf.out(ps.net), 'Costs in', ps && ps.count ? `Paid ${pf.out(ps.paid)}` : null]].map(([v, l, x]) => React.createElement("div", {
      key: l
    }, React.createElement("div", {
      style: {
        ...MONO,
        fontSize: 14,
        fontWeight: 500,
        color: T.coal
      }
    }, v), React.createElement("div", {
      style: {
        fontSize: 10,
        color: T.mist,
        marginTop: 2
      }
    }, l), x && React.createElement("div", {
      style: { ...MONO, fontSize: 10.5, color: T.slate, marginTop: 2 }
    }, x))));
  })(), p.sections.length === 0 && React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.mist,
      display: 'flex',
      alignItems: 'center',
      gap: 6
    }
  }, React.createElement(Icon.plug, {
    s: 11,
    c: T.mist
  }), " No lines yet \u2014 add them in Budget or import a spreadsheet"))), React.createElement("div", {
    className: "fade-up",
    style: {
      animationDelay: `${150 + projects.length * 60}ms`,
      background: 'transparent',
      padding: '14px 22px',
      cursor: 'pointer',
      border: `2px dashed ${T.rule}`,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      transition: 'border-color .2s,background .2s',
      color: T.mist
    },
    onClick: onAddProject,
    onMouseEnter: e => {
      e.currentTarget.style.borderColor = T.navy;
      e.currentTarget.style.background = T.white;
      e.currentTarget.style.color = T.navy;
    },
    onMouseLeave: e => {
      e.currentTarget.style.borderColor = T.rule;
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = T.mist;
    }
  }, React.createElement(Icon.plus, {
    s: 14,
    c: "currentColor"
  }), React.createElement("span", {
    style: {
      fontSize: 13,
      fontWeight: 500
    }
  }, "Add New Project")))), React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: T.mist,
      marginBottom: 14
    }
  }, "STATUS \xB7 ", project?.name?.toUpperCase() || 'ACTIVE PROJECT'), React.createElement("div", {
    style: {
      background: T.white,
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, Object.entries(stCounts).sort((a, b) => b[1] - a[1]).map(([st, cnt], idx) => React.createElement("div", {
    key: st,
    style: {
      padding: '12px 16px',
      borderBottom: `1px solid ${T.ivoryD}`,
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }
  }, React.createElement(Badge, {
    s: st,
    tiny: true
  }), React.createElement("span", {
    style: {
      ...MONO,
      fontSize: 12,
      color: T.coal,
      marginLeft: 'auto'
    }
  }, cnt), React.createElement("div", {
    style: {
      width: 56,
      height: 3,
      background: T.ivoryDD,
      borderRadius: 2,
      overflow: 'hidden',
      flexShrink: 0
    }
  }, React.createElement("div", {
    style: {
      height: '100%',
      width: `${cnt / allItems.length * 100}%`,
      background: ST[st]?.color || T.navy,
      borderRadius: 2
    }
  }))))), React.createElement("div", {
    style: {
      marginTop: 20,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: T.mist,
      marginBottom: 14
    }
  }, "SECTION COSTS \xB7 " + CUR.cost), React.createElement("div", {
    style: {
      background: T.white,
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, sections.length === 0 && React.createElement("div", {
    style: {
      padding: '16px',
      fontSize: 12,
      color: T.mist,
      textAlign: 'center'
    }
  }, "No data loaded"), sections.map(sec => {
    const t = secTotals(sec.items, fx);
    const pct = gt.inGBP > 0 ? t.inGBP / gt.inGBP : 0;
    return React.createElement("div", {
      key: sec.id,
      style: {
        padding: '10px 16px',
        borderBottom: `1px solid ${T.ivoryD}`
      }
    }, React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 5
      }
    }, React.createElement("span", {
      style: {
        fontSize: 10,
        color: T.slate
      }
    }, sec.name), React.createElement("span", {
      style: {
        ...MONO,
        fontSize: 10,
        color: T.coal
      }
    }, fmt.gbp(t.inGBP))), React.createElement("div", {
      style: {
        height: 2,
        background: T.ivoryDD,
        borderRadius: 1,
        overflow: 'hidden'
      }
    }, React.createElement("div", {
      style: {
        height: '100%',
        width: `${pct * 100}%`,
        background: T.navy,
        borderRadius: 1
      }
    })));
  })))));
}
